import type { Amenity, Space, SpaceKind } from './types';

/**
 * The five spaces, in the order the site lists them.
 *
 * Rates are not in here. A space has several packages and the cheapest one is
 * what a card shows ("from $8 / hour"), so the figure belongs with the packages
 * and the card derives it — see `priceFrom` in `./packages`.
 */
export const SPACES: Space[] = [
  {
    slug: 'coworking-desks',
    adminId: 'desks',
    title: 'Coworking Desks',
    kind: 'Desks',
    capacity: 'Open floor',
    blurb:
      'An open floor of desks with power, wifi and the café downstairs. Take one for a day or keep one for the year.',
    description:
      'The main floor is desks: no partitions, no assigned seats on the daily rate, and enough of them that walking in without a reservation works. Every bay has power and a locker, the wifi is the building line rather than a shared hotspot, and the café is one floor down when the screen has stopped being useful. Monthly and six-month holders keep the same desk and get the building outside opening hours.',
    cta: 'See the coworking floor',
    primaryPackage: 'daily-desk',
    packages: [
      'daily-desk',
      'weekly-desk',
      'monthly-desk',
      'six-month-desk-plus',
      'student-evening-desk',
    ],
    amenities: ['wifi', 'desk', 'power', 'locker', 'kitchen', 'parking'],
    hours: { open: 9, close: 22 },
    minHours: null,
    window: null,
    page: {
      title: 'Daily Desk',
      lead: 'One day on the open coworking floor. Turn up, pick a free desk, plug in.',
      about: [
        'The daily desk is the simplest way to use MDS. You get a seat anywhere on the open floor for the day, with power, fibre and access to the shared kitchen and café counter.',
        'Nothing is assigned, so the floor stays flexible: come in for a morning of focused work, stay for the day, or use it to try the space before committing to a month.',
      ],
      includes: [
        { icon: 'desk', label: 'A seat on the open coworking floor' },
        { icon: 'wifi', label: 'Fibre internet and power at every desk' },
        { icon: 'kitchen', label: 'Shared kitchen and café counter access' },
        { icon: 'locker', label: 'Lockers available on request' },
        { icon: 'print', label: 'Print and scan at the front desk' },
        { icon: 'parking', label: 'Free parking for one vehicle' },
      ],
      rules: {
        booking: 'No reservation needed. Sign in at the front desk on arrival.',
        hours: 'Any time during standard opening hours.',
        payment: 'Cash or card at the desk before you sit down.',
        cancellation: 'Nothing to cancel; unused days are not carried over.',
      },
    },
  },
  {
    slug: 'small-meeting-room',
    adminId: 'small',
    title: 'Small Meeting Room',
    kind: 'Rooms',
    capacity: '6 seats',
    blurb:
      'One table, six chairs and a door that closes. The room for a client call or a conversation the floor should not hear.',
    description:
      'Six seats around one table, a screen on the wall and a door that shuts properly. It is booked by the hour with no minimum, which makes it the room for a forty-minute client call as much as for an afternoon. Water and glasses are set out before you arrive; tell us if you want coffee and the café sends it up.',
    cta: 'Book the meeting room',
    primaryPackage: 'small-meeting-hour',
    packages: ['small-meeting-hour', 'small-meeting-half-day'],
    amenities: ['wifi', 'screen', 'whiteboard', 'power', 'parking'],
    hours: { open: 9, close: 22 },
    minHours: null,
    window: null,
  },
  {
    slug: 'meeting-training-room',
    adminId: 'training',
    title: 'Meeting & Training Room',
    kind: 'Rooms',
    capacity: '20 seats',
    blurb:
      'Twenty seats, a projector and a room that can be laid out four ways. Booked in runs of two hours or more.',
    description:
      'The teaching room. Twenty stackable chairs, tables that come out or go into a U, a projector with an HDMI run to the lectern, and a flipchart that gets refilled. Two hours is the shortest booking because the layout has to be set before and reset after, and that is time somebody spends. Tell us the shape you want when you book and it is standing when you walk in.',
    cta: 'Book the training room',
    primaryPackage: 'training-hour',
    packages: ['training-hour', 'training-day'],
    amenities: ['wifi', 'projector', 'whiteboard', 'power', 'kitchen', 'parking'],
    hours: { open: 9, close: 22 },
    minHours: 2,
    window: null,
  },
  {
    slug: 'cafe-hall',
    adminId: 'hall',
    title: 'MDS Café Hall',
    kind: 'Café hall',
    capacity: 'Up to 120',
    blurb:
      'The ground-floor hall, hired half-day or for the full evening. Chairs or tables, a PA and the café counter open through it.',
    description:
      'The whole ground floor, hired as a block rather than by the hour. The full evening runs 2 PM to 10 PM and that includes the time it takes to set the room and clear it, and a half-day is four hours inside the same window. Each block is priced twice: chairs alone, or chairs and tables. The PA, the projector and the counter come with the room, catering is per head, and a deposit holds the date.',
    cta: 'Hire the café hall',
    primaryPackage: 'cafe-hall-evening-chairs',
    packages: [
      'cafe-hall-half-day-chairs',
      'cafe-hall-half-day-tables',
      'cafe-hall-evening-chairs',
      'cafe-hall-evening-tables',
    ],
    amenities: ['wifi', 'projector', 'sound', 'cafe', 'power', 'parking'],
    hours: null,
    minHours: null,
    window: [14, 22],
  },
  {
    slug: 'creative-studio',
    adminId: 'studio',
    title: 'Podcast / Creative Studio',
    kind: 'Studio',
    capacity: '8 seats',
    blurb:
      'A treated room with four mics and a camera. Run it yourself by the hour, or book an operator with it.',
    description:
      'A treated room built for voice: four microphones on booms, a desk that records each of them separately, headphones for everyone at the table and a camera on a locked-off shot if you want the video too. Self-operated by the hour once you have been shown the desk. If you would rather not touch it, add an operator and you leave with the files.',
    cta: 'Book the studio',
    primaryPackage: 'studio-hour',
    packages: ['studio-hour', 'studio-production-day'],
    amenities: ['wifi', 'sound', 'camera', 'power', 'parking'],
    hours: { open: 9, close: 22 },
    minHours: null,
    window: null,
  },
];

/**
 * The filter chips on `/spaces`, derived rather than declared. "All spaces" is
 * the component's own affordance, not a kind, so it is not in here.
 */
export const SPACE_KINDS: SpaceKind[] = [...new Set(SPACES.map((s) => s.kind))];

/**
 * The pill a single space wears on its detail page and in "Also available".
 * Singular, and named for what you hire rather than how the index groups it —
 * the filter says "Rooms", one card says "Room".
 */
export const KIND_BADGE: Record<SpaceKind, string> = {
  Desks: 'Desk package',
  Rooms: 'Room',
  'Café hall': 'Event hire',
  Studio: 'Studio',
};

/**
 * Amenity labels. The icon is picked by the component from `id`, because an
 * icon is a rendering decision and this file is content — the same `wifi` row
 * is a glyph on the detail page and a plain word in a list on the booking page.
 */
export const AMENITIES: Amenity[] = [
  { id: 'wifi', label: 'Fibre wifi' },
  { id: 'desk', label: 'Desk and chair' },
  { id: 'power', label: 'Power at every seat' },
  { id: 'locker', label: 'Lockers' },
  { id: 'kitchen', label: 'Kitchen access' },
  { id: 'cafe', label: 'Café counter' },
  { id: 'screen', label: 'Wall screen' },
  { id: 'projector', label: 'Projector' },
  { id: 'whiteboard', label: 'Whiteboard' },
  { id: 'sound', label: 'PA and sound desk' },
  { id: 'camera', label: 'Camera' },
  { id: 'parking', label: 'Free parking' },
];

/** Look a space up by its public slug. */
export const getSpace = (slug: string): Space | undefined =>
  SPACES.find((space) => space.slug === slug);

/** Look a space up by the short code the admin app books against. */
export const getSpaceByAdminId = (adminId: string): Space | undefined =>
  SPACES.find((space) => space.adminId === adminId);

/** Resolve a space's amenity ids to rows, dropping any id that no longer exists. */
export const amenitiesOf = (space: Space): Amenity[] =>
  space.amenities
    .map((id) => AMENITIES.find((a) => a.id === id))
    .filter((a): a is Amenity => a !== undefined);
